/**
 * How states are named on screen and in what a picker writes.
 *
 * Used wherever a list of states is offered, so they are named one way
 * everywhere (issue #42).
 */

import type { EntityRow } from '../../schema'

/**
 * What each state is called on screen, by identifier.
 *
 * THE SHORT NAME, and the file keeps the qualified one (Patrick, 2026-09-15,
 * #42): the server writes whatever it is sent as a qualified reference.
 *
 * Two states can share a short name - nested machines each with an `Idle` - and
 * two identical entries in a picker are two chances to pick the wrong one. A
 * collision falls back to `Owner › Name`, and to the identifier if even that
 * collides.
 */
export function stateLabels(states: EntityRow[]): Map<string, string> {
  const byId = new Map(states.map((row) => [row.id, row]))
  const nameOf = (row: EntityRow) => String(row.name ?? row.id)
  const count = (labels: string[], label: string) => labels.filter((l) => l === label).length

  const short = states.map(nameOf)
  const scoped = states.map((row, index) => {
    if (count(short, short[index]) === 1) return short[index]
    const owner = row.owner ? byId.get(String(row.owner)) : undefined
    return owner ? `${nameOf(owner)} › ${short[index]}` : short[index]
  })
  return new Map(states.map((row, index) => [
    row.id,
    count(scoped, scoped[index]) === 1 ? scoped[index] : row.id,
  ]))
}
